import { useState, useEffect } from 'react';
import { database, ref, onValue, off } from '../lib/firebase';
import { Eye } from 'lucide-react';
import { GAMES } from '../games/registry';
import { getAvatarById } from './AvatarPicker';
import { Link } from 'react-router-dom';

interface ActiveGame {
  id: string;
  gameType: string;
  hostUsername?: string;
  hostAvatar?: string;
  guestUsername?: string;
  guestAvatar?: string;
  createdAt: number;
}

export default function SpectatorList() {
  const [games, setGames] = useState<ActiveGame[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const gamesRef = ref(database, 'games');
    onValue(gamesRef, (snapshot) => {
      if (!snapshot.exists()) {
        setGames([]);
        setLoading(false);
        return;
      }

      const data = snapshot.val();
      const active: ActiveGame[] = [];

      for (const [id, game] of Object.entries(data) as [string, any][]) {
        if (game.status === 'playing' && game.guest) {
          active.push({
            id,
            gameType: game.gameType,
            hostUsername: game.hostUsername,
            hostAvatar: game.hostAvatar,
            guestUsername: game.guestUsername,
            guestAvatar: game.guestAvatar,
            createdAt: game.createdAt,
          });
        }
      }

      // Sort newest first
      active.sort((a, b) => b.createdAt - a.createdAt);
      setGames(active);
      setLoading(false);
    });

    return () => off(gamesRef);
  }, []);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pink-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-pink-500 via-violet-500 to-blue-500 text-transparent bg-clip-text">
          Watch Live Games
        </h1>
        <p className="text-gray-300">Spectate games in progress</p>
      </div>

      {games.length === 0 ? (
        <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl p-8 border border-white/10 text-center">
          <Eye className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-300">No games in progress right now.</p>
          <Link
            to="/play"
            className="inline-block mt-4 px-6 py-2 bg-gradient-to-r from-pink-600 to-violet-600 rounded-lg hover:opacity-90 transition"
          >
            Start a Game
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {games.map(game => {
            const gameDef = GAMES.find(g => g.id === game.gameType);
            const hostAvatar = getAvatarById(game.hostAvatar);
            const guestAvatar = getAvatarById(game.guestAvatar);

            return (
              <Link
                key={game.id}
                to={`/watch/${game.id}`}
                className="block bg-slate-800/50 backdrop-blur-lg rounded-xl p-4 border border-white/10 hover:border-pink-500/30 transition"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-2xl">{gameDef?.icon || '🎮'}</div>
                    <div>
                      <div className="font-medium text-sm text-gray-300">{gameDef?.name || game.gameType}</div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="flex items-center gap-1">
                          {hostAvatar && <span>{hostAvatar.emoji}</span>}
                          <span className="text-white">{game.hostUsername || 'Player 1'}</span>
                        </span>
                        <span className="text-gray-300">vs</span>
                        <span className="flex items-center gap-1">
                          {guestAvatar && <span>{guestAvatar.emoji}</span>}
                          <span className="text-white">{game.guestUsername || 'Player 2'}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-pink-400 text-sm">
                    <Eye className="w-4 h-4" />
                    Watch
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
